/**
 * Shared defaults for North American craft-malthouse entries.
 *
 * These houses malt small lots from regional grain, so numbers are typically
 * class-typical rather than lot-exact and are marked `representative` unless a
 * published analysis is passed in. Colour is stored in °L; EBC is derived with
 * `EBC = 2.65·°L − 1.2` unless a maltster-published EBC is supplied. None of
 * the craft houses we model publish distilled-water mash pH or titratable
 * buffering, so those fields stay `null` and the malt-class defaults apply.
 */

import { type Fermentable, lovibondToEbc } from '../../../schema/fermentable';

/** Fields a regional file must supply; the rest have honest defaults. */
export type CraftMaltInput = Omit<
  Fermentable,
  'origin' | 'diPh' | 'bufferingCapacity' | 'colorEbc' | 'provenance'
> & {
  origin?: Fermentable['origin'];
  diPh?: Fermentable['diPh'];
  bufferingCapacity?: Fermentable['bufferingCapacity'];
  colorEbc?: Fermentable['colorEbc'];
  provenance?: Fermentable['provenance'];
};

/**
 * Build a craft-malt row. `origin` defaults to the United States; the Canadian
 * and Mexican files pass their own.
 *
 * @param input - Product fields from a regional file.
 * @returns A complete {@link Fermentable} with EBC derived from °L.
 */
export function craftMalt(input: CraftMaltInput): Fermentable {
  return {
    origin: 'United States',
    diPh: null,
    bufferingCapacity: null,
    provenance: 'representative',
    ...input,
    colorEbc: input.colorEbc ?? Math.round(lovibondToEbc(input.colorLovibond) * 10) / 10,
  };
}
